import { useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import type { UseFormReturn } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod/v4';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  DROPDOWN_FETCH_PAGE_SIZE,
  useApiClient,
  useCurrentUser,
  useDebouncedValue,
  usePermissions,
  UNIQUENESS_CHECK_DEBOUNCE_MS,
} from '@klareospace/pharos-lib-front-utils';
import {
  CURRENCIES,
  DEFAULT_DURATION_TIME_MINUTES,
  HANDLING_UNIT_TEMPERATURES,
  ORDER_TYPE_SYSTEMIC_CODES,
  VOLUME_UNITS,
  WEIGHT_UNITS,
  type Customer,
  type HandlingUnitTemperature,
  type OrderType,
  type Site,
  type SiteWithSpaceName,
  type VolumeUnit,
  type WeightUnit,
} from '@klareospace/pharos-lib-types';
import { isDefined } from '@klareospace/pharos-lib-utils';
import type { DateRangeValue } from '@klareospace/pharos-lib-web-utils';

import { buildAddressEntry, useUpdateCustomer } from '&/features/customers';
import type { AddressBookEntryInput } from '&/features/customers';
import { createOrdersApi, orderKeys } from '&/features/orders/api';
import { useOrderTypes } from '&/features/settings/order-types';
import { useSites } from '&/features/settings/sites/hooks';
import { useUserScopes } from '&/hooks/use-user-scopes';
import { useMasterContext } from '&/master';

import {
  buildDeliveryAddressEntry,
  isRouteInformationComplete,
  mergeCustomerAddress,
  resolvePreselectedSite,
  resolveRouteInformationErrorMessageKey,
} from '../utils';
import type {
  ContentSectionFormInput,
  OrderCreationFormInput,
  RouteInformationFormInput,
  SchedulingSectionFormInput,
} from '../schemas/order-creation.schema';
import type { ShipperOption } from './use-shipper-options';

// ─── Constants ────────────────────────────────────────────────────────────────

/** Content slice defaults — also the single shared fixture for specs across this feature. */
export const DEFAULT_CONTENT: ContentSectionFormInput = {
  currency: CURRENCIES[0],
  handlingUnits: [],
  orderValue: '',
  tags: [],
  totalQuantity: '',
  totalVolume: '',
  totalWeight: '',
  volumeUnit: VOLUME_UNITS.L,
  weightUnit: WEIGHT_UNITS.KG,
};

/** Route information slice defaults — also the single shared fixture for specs across this feature. */
export const DEFAULT_ROUTE_INFORMATION: RouteInformationFormInput = {
  deliveryAddress: null,
  deliveryDurationTime: DEFAULT_DURATION_TIME_MINUTES,
  pickupDurationTime: DEFAULT_DURATION_TIME_MINUTES,
  pickupTimeSlot: { enabled: false, value: null },
  selectedSite: null,
};

/** Scheduling slice defaults — also the single shared fixture for specs across this feature. */
export const DEFAULT_SCHEDULING: SchedulingSectionFormInput = {
  carrierCode: '',
  deliveryTimeSlot: null,
};

const DEFAULT_VALUES: OrderCreationFormInput = {
  content: DEFAULT_CONTENT,
  customer: { selectedCustomer: null },
  generalInformation: { orderTypeId: '', referenceNumber: '', shipperCode: '' },
  routeInformation: DEFAULT_ROUTE_INFORMATION,
  scheduling: DEFAULT_SCHEDULING,
};

const DUPLICATE_CHECK_PARAMS = { page: 1, pageSize: 1 };

const POSITIVE_INTEGER_REGEX = /^\d+$/;

// ─── Types ────────────────────────────────────────────────────────────────────

export interface UseOrderCreationFormOptions {
  /** Whether the drawer is currently open — drives default-value (re)application on each open. */
  open: boolean;
  /** Active shipper configurations from `useShipperOptions` — drives the Shipper field's auto-selection and required-ness. */
  shipperOptions: ShipperOption[];
}

export interface UseOrderCreationFormReturn {
  /** Whether the current user can list sites — drives the Pickup block's permission-denied message. */
  canListSites: boolean;
  form: UseFormReturn<OrderCreationFormInput>;
  orderTypes: OrderType[];
  /** Sites scoped to the current user. */
  sites: SiteWithSpaceName[];
  /**
   * Whether `syncAddressBook`'s PATCH is in flight — drives the address dialog's save button so a
   * second save can't fire while an earlier one is still pending (and racing it on resolution).
   */
  isSyncingAddressBook: boolean;
  /**
   * PATCHes the selected customer's address book from an edited/added delivery address entry,
   * then writes the saved entry's index back onto `routeInformation.deliveryAddress.sourceIndex`
   * so a later edit replaces it instead of appending a duplicate. Owned at the drawer's lifetime
   * (not the Route information section's) so the mutation's toast/write-back still land even if
   * the user navigates to the next section before the PATCH resolves.
   */
  syncAddressBook: (entry: AddressBookEntryInput) => void;
  /**
   * Validates the Content slice; resolves false while invalid (e.g. a negative free-entry value)
   * or while none of the three completion paths (quantity / handling units / weight+volume) hold.
   */
  validateContent: () => Promise<boolean>;
  /** Validates the Customer slice; resolves false while no customer is selected. */
  validateCustomer: () => Promise<boolean>;
  /**
   * Validates the General information slice; resolves false while invalid, a duplicate reference
   * error is set, or a shipper is required (shipper options exist) but none is selected yet.
   */
  validateGeneralInformation: () => Promise<boolean>;
  /**
   * Validates the Route information slice; resolves false while invalid (e.g. a negative duration,
   * or an enabled pickup timeslot with start >= end) or incomplete (no pickup site or no delivery
   * address yet).
   */
  validateRouteInformation: () => Promise<boolean>;
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

/**
 * Owns the manual order creation form — all four slices (General information, Customer, Route
 * information, Content). `FormProvider` wraps the whole drawer body so every section shares the
 * same `OrderCreationFormInput` instance.
 */
export function useOrderCreationForm({
  open,
  shipperOptions,
}: UseOrderCreationFormOptions): UseOrderCreationFormReturn {
  const { t } = useTranslation('order-creation');
  const { hasPermission } = usePermissions();
  const canListSites = hasPermission('sites:list');
  const { data: orderTypesData } = useOrderTypes({ page: 1, pageSize: DROPDOWN_FETCH_PAGE_SIZE });
  const orderTypes = useMemo(() => orderTypesData?.items ?? [], [orderTypesData]);

  const masterContext = useMasterContext();
  // Scoped to the master-selector space (when one is set, with or without a site chosen within
  // it) so a space-scoped user only sees — and the "exactly one site in scope" pre-selection rule
  // only evaluates — the sites that belong to that space, mirroring the main-selector's own
  // SitesPanel site search.
  const { data: sitesData } = useSites({
    enabled: canListSites,
    page: 1,
    pageSize: DROPDOWN_FETCH_PAGE_SIZE,
    spaceIds: masterContext.spaceId ? [masterContext.spaceId] : [],
  });
  const sites = useMemo(() => sitesData?.items ?? [], [sitesData]);
  const { user } = useCurrentUser();
  const { singleSiteId } = useUserScopes(user);
  const updateCustomer = useUpdateCustomer();

  const schema = useMemo(
    () =>
      z.object({
        content: z.object({
          currency: z.enum(CURRENCIES),
          handlingUnits: z.array(
            z.object({
              barcode: z.string(),
              quantity: z.number().int().min(1, t('sections.content.fields.handlingUnits.quantityInvalid')),
              temperature: z.enum(
                Object.values(HANDLING_UNIT_TEMPERATURES) as [HandlingUnitTemperature, ...HandlingUnitTemperature[]],
              ),
              typeCode: z.string().trim().min(1),
            }),
          ),
          orderValue: z.string().trim().refine(isPositiveOrEmpty, t('sections.content.fields.orderValue.invalid')),
          tags: z.array(z.string()),
          totalQuantity: z
            .string()
            .trim()
            .refine(isPositiveIntegerOrEmpty, t('sections.content.fields.totalQuantity.invalid')),
          totalVolume: z.string().trim().refine(isPositiveOrEmpty, t('sections.content.fields.totalVolume.invalid')),
          totalWeight: z.string().trim().refine(isPositiveOrEmpty, t('sections.content.fields.totalWeight.invalid')),
          volumeUnit: z.enum(Object.values(VOLUME_UNITS) as [VolumeUnit, ...VolumeUnit[]]),
          weightUnit: z.enum(Object.values(WEIGHT_UNITS) as [WeightUnit, ...WeightUnit[]]),
        }),
        customer: z.object({
          selectedCustomer: z.custom<Customer | null>((value) => value != null, {
            message: t('sections.customer.fields.required'),
          }),
        }),
        // shipperCode is deliberately unconstrained here — its required-ness depends on whether any
        // shipper option exists at all, which is checked as a plain predicate in
        // `validateGeneralInformation` (mirroring routeInformation/scheduling below), not a zod rule.
        generalInformation: z.object({
          orderTypeId: z.string().trim().min(1, t('sections.generalInformation.fields.orderType.required')),
          referenceNumber: z.string().trim(),
          shipperCode: z.string(),
        }),
        // selectedSite/deliveryAddress are deliberately unconstrained here (no "required" rule) —
        // completion is checked by a plain predicate (validateRouteInformation), mirroring Content,
        // not Customer's zod-required pattern, so the section can show its own incomplete message.
        routeInformation: z.object({
          deliveryAddress: z.custom<RouteInformationFormInput['deliveryAddress']>(() => true),
          deliveryDurationTime: z.number().min(0),
          pickupDurationTime: z.number().min(0),
          pickupTimeSlot: z.object({
            enabled: z.boolean(),
            value: z.custom<DateRangeValue | null>(() => true).nullable(),
          }),
          selectedSite: z.custom<Site | null>(() => true),
        }),
        // Permissive, mirroring routeInformation above — completion is checked by a plain predicate
        // (isSchedulingComplete), not a zod rule, so the section can show its own section-level error.
        scheduling: z.object({
          carrierCode: z.string(),
          deliveryTimeSlot: z.custom<DateRangeValue | null>(() => true).nullable(),
        }),
      }),
    [t],
  );

  const form = useForm<OrderCreationFormInput>({
    defaultValues: DEFAULT_VALUES,
    mode: 'onChange',
    resolver: zodResolver(schema),
  });

  const { clearErrors, getFieldState, getValues, setError, setValue } = form;

  // ─── Reset the reference to its default every time the drawer opens ─────
  // `setValue` (not `resetField`) is used deliberately: the General information section's
  // Controller may not be mounted yet while the section is closed, and `resetField` is a no-op
  // for an unregistered field.

  useEffect(() => {
    if (!open) return;
    setValue('generalInformation.referenceNumber', DEFAULT_VALUES.generalInformation.referenceNumber);
    setValue('generalInformation.shipperCode', DEFAULT_VALUES.generalInformation.shipperCode);
    setValue('customer.selectedCustomer', DEFAULT_VALUES.customer.selectedCustomer);
    setValue('content', DEFAULT_CONTENT);
    setValue('routeInformation', DEFAULT_ROUTE_INFORMATION);
    setValue('scheduling', DEFAULT_SCHEDULING);
  }, [open, setValue]);

  // ─── Pre-fill order type with the systemic Cart Drop Off type ───────────

  useEffect(() => {
    if (!open || orderTypes.length === 0) return;
    if (getValues('generalInformation.orderTypeId')) return;
    const cartDropOff = orderTypes.find((type) => type.code === ORDER_TYPE_SYSTEMIC_CODES.CART_DROP_OFF);
    const defaultOrderType = cartDropOff ?? orderTypes[0];
    setValue('generalInformation.orderTypeId', defaultOrderType.id, { shouldValidate: true });
  }, [getValues, open, orderTypes, setValue]);

  // ─── Clear a stale shipper pick, then auto-select when exactly one option is configured ─────
  // A previously selected/auto-selected `shipperCode` that no longer appears among the active
  // `shipperOptions` (e.g. the configuration was deactivated while the drawer stayed open) must be
  // cleared — otherwise a stale code would silently ride along into the submitted payload. Clearing
  // never touches a code that is still a valid option, so a still-valid manual pick survives.
  // Auto-selection then runs against the (possibly just-cleared) value: only while the field is
  // empty and exactly one option remains — re-running on each open (the reset effect above clears
  // it to '' first) and the moment a single-option list loads asynchronously after the drawer is
  // already open.

  useEffect(() => {
    if (!open) return;
    const currentShipperCode = getValues('generalInformation.shipperCode');
    const isStale = currentShipperCode !== '' && !shipperOptions.some((option) => option.code === currentShipperCode);
    if (isStale) setValue('generalInformation.shipperCode', '', { shouldValidate: true });

    const shipperCode = isStale ? '' : currentShipperCode;
    if (shipperOptions.length !== 1 || shipperCode) return;
    setValue('generalInformation.shipperCode', shipperOptions[0].code, { shouldValidate: true });
  }, [getValues, open, setValue, shipperOptions]);

  // ─── Pre-select the pickup site ──────────────────────────────────────────
  // Master-selector site wins when set; else the single site a site-scoped user has access to;
  // else the one site left when exactly one is in scope; else left empty for the user to pick.
  // Only applies while no site has been selected yet (manual choices are never overridden).

  useEffect(() => {
    if (!open || sites.length === 0) return;
    if (getValues('routeInformation.selectedSite')) return;
    const preselected = resolvePreselectedSite({ masterSiteId: masterContext.siteId, singleSiteId, sites });
    if (preselected) setValue('routeInformation.selectedSite', preselected, { shouldValidate: true });
  }, [getValues, masterContext.siteId, open, setValue, singleSiteId, sites]);

  // ─── Pre-fill the delivery address from the selected customer ───────────
  // Preferred = the address flagged `location.isDefault`, falling back to the first one. Only
  // recomputed when the customer's `id` actually changes (a new pick, or the customer cleared), or
  // when there is no delivery address yet — a manually edited address must survive both re-picking
  // the same customer and the address-book sync write-back (see `syncAddressBook` below), which
  // both write `customer.selectedCustomer` again without changing which customer is selected.

  const selectedCustomer = form.watch('customer.selectedCustomer');
  const previousCustomerIdRef = useRef<string | null>(null);

  useEffect(() => {
    const previousCustomerId = previousCustomerIdRef.current;
    const currentCustomerId = selectedCustomer?.id ?? null;
    previousCustomerIdRef.current = currentCustomerId;

    const customerChanged = previousCustomerId !== currentCustomerId;
    if (!customerChanged && isDefined(getValues('routeInformation.deliveryAddress'))) return;

    const entry = selectedCustomer ? buildDeliveryAddressEntry(selectedCustomer) : null;
    setValue('routeInformation.deliveryAddress', entry, { shouldValidate: true });
  }, [getValues, selectedCustomer, setValue]);

  // ─── Address-book sync (drawer-lifetime, survives the Route information section unmounting) ───

  /**
   * Rebuilds the full address array from the freshest `customer.selectedCustomer` (never a render
   * closure) so a fast double-edit never PATCHes against stale data, and records where the saved
   * entry landed so a later edit of the same entry replaces it instead of appending a duplicate.
   * A second call while a PATCH is already pending is ignored — the caller (the address dialog's
   * Save button) is disabled via `isSyncingAddressBook` for the same reason, this is belt-and-suspenders.
   * The `onSuccess` write-back only applies if the selected customer is still the one the PATCH was
   * issued for — otherwise the user switched customers while the request was in flight and an older
   * response must not overwrite the newer selection.
   */
  function syncAddressBook(entry: AddressBookEntryInput): void {
    if (updateCustomer.isPending) return;
    const customer = getValues('customer.selectedCustomer');
    if (!customer || !hasPermission('customers:manage')) return;

    const existingAddresses = customer.addresses;
    const built = buildAddressEntry(entry, existingAddresses);
    const nextAddresses = mergeCustomerAddress(existingAddresses, entry.sourceIndex, built);
    const savedIndex = entry.sourceIndex ?? existingAddresses.length;

    updateCustomer.mutate(
      { id: customer.id, patch: { addresses: nextAddresses } },
      {
        onError: (): void => {
          toast.error(t('sections.routeInformation.delivery.syncError'));
        },
        onSuccess: (updatedCustomer): void => {
          if (getValues('customer.selectedCustomer')?.id !== customer.id) return;
          setValue('customer.selectedCustomer', updatedCustomer);
          setValue('routeInformation.deliveryAddress.sourceIndex', savedIndex);
        },
      },
    );
  }

  // ─── Debounced duplicate reference check ─────────────────────────────────

  // The zod schema trims `referenceNumber` on output, so the duplicate check must run against the
  // same trimmed value — otherwise a trailing-whitespace reference (e.g. 'DUP-1 ') bypasses the
  // live check and only surfaces as a 409 at create time.
  const referenceNumber = form.watch('generalInformation.referenceNumber');
  const trimmedReferenceNumber = referenceNumber.trim();
  const debouncedReferenceNumber = useDebouncedValue(trimmedReferenceNumber, UNIQUENESS_CHECK_DEBOUNCE_MS);
  const client = useApiClient('deliveries');
  const ordersApi = useMemo(() => createOrdersApi(client), [client]);

  const { data: duplicateCheckData } = useQuery({
    enabled: !!debouncedReferenceNumber,
    gcTime: 60_000,
    queryFn: () => ordersApi.searchOrders({ ...DUPLICATE_CHECK_PARAMS, referenceNumber: debouncedReferenceNumber }),
    queryKey: orderKeys.list({ ...DUPLICATE_CHECK_PARAMS, referenceNumber: debouncedReferenceNumber }),
    staleTime: 30_000,
  });

  useEffect(() => {
    if (!debouncedReferenceNumber || debouncedReferenceNumber !== trimmedReferenceNumber) return;
    if (getFieldState('generalInformation.referenceNumber').error?.type === 'manual') return;
    if (duplicateCheckData && duplicateCheckData.items.length > 0) {
      setError('generalInformation.referenceNumber', {
        message: t('sections.generalInformation.fields.reference.duplicateError'),
        type: 'manual',
      });
    }
  }, [debouncedReferenceNumber, duplicateCheckData, getFieldState, setError, t, trimmedReferenceNumber]);

  // Manual errors set via `setError` are not cleared by the resolver's own revalidation, so the
  // duplicate error is explicitly cleared on every live edit of the trimmed value — it reappears
  // only if the next debounced check still finds a match.
  useEffect(() => {
    clearErrors('generalInformation.referenceNumber');
  }, [trimmedReferenceNumber, clearErrors]);

  // ─── Validation gate for the Next button ─────────────────────────────────

  // `form.trigger` runs the zod resolver, which replaces the whole errors object and would wipe
  // the manual duplicate error — so that check must happen first and short-circuit. The shipper
  // requirement is re-evaluated (set or cleared) on every call, mirroring how `validateCustomer`
  // re-evaluates the "required" error via `form.trigger` on every Next/submit attempt — a shipper is
  // only required at all when at least one option exists (`shipperOptions.length > 0`); none/forbidden/
  // error block submission separately (see `useSubmitOrder`'s `shipperOptionsStatus` gate).
  async function validateGeneralInformation(): Promise<boolean> {
    if (getFieldState('generalInformation.referenceNumber').error?.type === 'manual') return false;
    const isOrderTypeValid = await form.trigger('generalInformation.orderTypeId');
    if (!isOrderTypeValid) return false;
    if (shipperOptions.length === 0 || getValues('generalInformation.shipperCode')) {
      clearErrors('generalInformation.shipperCode');
      return true;
    }
    setError('generalInformation.shipperCode', {
      message: t('sections.generalInformation.fields.shipper.required'),
      type: 'manual',
    });
    return false;
  }

  function validateCustomer(): Promise<boolean> {
    return form.trigger('customer.selectedCustomer');
  }

  // Plain predicate (not a zod `superRefine`): completion is checked separately from schema
  // validation so it can surface its own section-level message instead of attaching to one field.
  // The manual error is re-evaluated (set or cleared) on every call, mirroring how `validateCustomer`
  // re-evaluates the "required" error via `form.trigger` on every Next click.
  async function validateContent(): Promise<boolean> {
    const isValid = await form.trigger('content');
    if (!isValid) return false;
    if (isContentComplete(getValues('content'))) {
      clearErrors('content');
      return true;
    }
    setError('content', { message: t('sections.content.incomplete'), type: 'manual' });
    return false;
  }

  // Same plain-predicate pattern as validateContent — completion (site + delivery address + a
  // valid-or-disabled timeslot) is a section-level concern, not a per-field zod rule. When the
  // site and address are both present and only the timeslot range is invalid, the invalid-range
  // message is shown instead of the generic "select a site and an address" one. A failed
  // `form.trigger` (e.g. a nested field rejecting an out-of-range value) still falls back to the
  // same section-level message instead of returning false silently.
  async function validateRouteInformation(): Promise<boolean> {
    const isValid = await form.trigger('routeInformation');
    const route = getValues('routeInformation');
    if (isValid && isRouteInformationComplete(route)) {
      clearErrors('routeInformation');
      return true;
    }
    setError('routeInformation', { message: t(resolveRouteInformationErrorMessageKey(route)), type: 'manual' });
    return false;
  }

  return {
    canListSites,
    form,
    isSyncingAddressBook: updateCustomer.isPending,
    orderTypes,
    sites,
    syncAddressBook,
    validateContent,
    validateCustomer,
    validateGeneralInformation,
    validateRouteInformation,
  };
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

/** Entered-and-positive, or left empty — negative/zero entries are invalid, empty is not an error. */
export function isPositiveOrEmpty(value: string): boolean {
  return value.trim() === '' || isPositive(value);
}

export function isPositive(value: string): boolean {
  const parsed = Number(value);
  return value.trim() !== '' && Number.isFinite(parsed) && parsed > 0;
}

/** Positive whole number only — rejects decimals ('2.5') and exponent notation ('1e2'). */
export function isPositiveInteger(value: string): boolean {
  return POSITIVE_INTEGER_REGEX.test(value.trim()) && isPositive(value);
}

/** Entered-and-a-positive-integer, or left empty. */
export function isPositiveIntegerOrEmpty(value: string): boolean {
  return value.trim() === '' || isPositiveInteger(value);
}

/** Complete iff totalQuantity >= 1, OR >= 1 handling unit line (type + quantity >= 1), OR weight AND volume both > 0. */
function isContentComplete(content: ContentSectionFormInput): boolean {
  const hasQuantity = isPositive(content.totalQuantity);
  const hasHandlingUnits =
    content.handlingUnits.length > 0 &&
    content.handlingUnits.every((line) => line.typeCode !== '' && line.quantity >= 1);
  const hasWeightAndVolume = isPositive(content.totalWeight) && isPositive(content.totalVolume);
  return hasQuantity || hasHandlingUnits || hasWeightAndVolume;
}
